import { Component, OnInit, inject, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { MatTableModule } from '@angular/material/table';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatCheckbox, MatCheckboxModule } from '@angular/material/checkbox';

import { UsersService } from '../../service/entity-services/users.service';
import { PermissionsStoreService } from '../../service/permissions/permissions-store.service';
import { PermissionsClientService } from '../../service/permissions/permissions-client.service';
import { AddUserDialogComponent } from './add-user-dialog/add-user-dialog.component';
import { EditUserNameDialogComponent } from './edit-user-name-dialog/edit-user-name-dialog.component';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { normalizeEmail } from '../../shared/utils/normalize-email';
import { EMAIL_CATEGORIES, EmailCategory } from '../../shared/models/email-categories.model';
import { environment } from '../../../environments/environment';
import { firstValueFrom } from 'rxjs';

interface ManagedUser {
  email: string;
  shortFriendlyName?: string;
  firstName?: string;
  lastName?: string;
  role?: string | null;
  activated: boolean;
  emailOptOuts: string[];
}

/**
 * Admin screen to activate/deactivate users. The user list is sourced from
 * {org}-Users (backend), which is populated automatically as people log in for
 * the first time (backend auto-provisions them as activated=false) — so the admin
 * never maintains a roster. Toggling Activated calls the set-activation endpoint.
 *
 * isAdmin() is a UX gate only; the backend authoritatively rejects non-admins.
 */
@Component({
  selector: 'app-manage-users',
  templateUrl: './manage-users.component.html',
  styleUrls: ['./manage-users.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatSnackBarModule,
    MatDialogModule,
    MatMenuModule,
    MatCheckboxModule,
  ],
})
export class ManageUsersComponent implements OnInit {
  private usersService = inject(UsersService);
  private permissionsClient = inject(PermissionsClientService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  readonly permissionsStore = inject(PermissionsStoreService);

  // Timesheet/timecard categories only for orgs that have those features.
  readonly emailCategories = EMAIL_CATEGORIES.filter(c =>
    !c.featureFlag || (environment as any)[c.featureFlag] === true);
  // The Emails column appears only for orgs wired to the opt-out endpoint, so
  // nobody gets a control that can't save.
  readonly emailControlsAvailable = !!(environment as any).URL_USERS_NT_SET_EMAIL_OPT_OUTS;
  readonly displayedColumns = this.emailControlsAvailable
    ? ['name', 'email', 'role', 'activated', 'emails', 'actions']
    : ['name', 'email', 'role', 'activated', 'actions'];

  private allUsers = signal<ManagedUser[]>([]);
  filter = signal<string>('');
  loading = signal<boolean>(true);
  // Emails with an in-flight toggle, to disable that row's control.
  savingEmails = signal<Set<string>>(new Set());
  // Emails currently being deleted, to disable that row's delete button.
  deletingEmails = signal<Set<string>>(new Set());
  // The signed-in admin's own email, so we can prevent self-delete.
  private currentUserEmail: string | null = null;
  // Ensures the admin-gated roster fetch runs at most once.
  private rosterFetched = false;

  constructor() {
    this.usersService.currentUserEmail$.subscribe(email => this.currentUserEmail = email);

    // Only admins may load the user roster. Gate the fetch (defense in depth) so a
    // non-admin who opens this page never even requests the user list. This is NOT
    // the security boundary — the backend authoritatively gates the hc-users
    // endpoints — but it keeps the app from pulling data the viewer isn't entitled
    // to. An effect (not ngOnInit) because the role can resolve after construction.
    effect(() => {
      if (this.permissionsStore.isAdmin() && !this.rosterFetched) {
        this.rosterFetched = true;
        this.usersService.refreshAllUsersRecords();
      }
    });
  }

  readonly filteredUsers = computed(() => {
    const term = this.filter().toLowerCase().trim();
    const users = this.allUsers();
    if (!term) return users;
    return users.filter(u =>
      (u.email || '').toLowerCase().includes(term) ||
      (u.shortFriendlyName || '').toLowerCase().includes(term));
  });

  ngOnInit(): void {
    this.usersService.allUsers$.subscribe((users: any[]) => {
      this.allUsers.set((users || []).map(u => ({
        email: u.email,
        shortFriendlyName: u.shortFriendlyName,
        firstName: u.firstName ?? '',
        lastName: u.lastName ?? '',
        role: u.role ?? null,
        activated: u.activated === true,
        emailOptOuts: Array.isArray(u.emailOptOuts) ? u.emailOptOuts : [],
      })));
      this.loading.set(false);
    });
    // The backend-sourced roster fetch is admin-gated in the constructor effect.
  }

  isSaving(email: string): boolean {
    return this.savingEmails().has(email);
  }

  /**
   * Opens the Add User dialog to pre-add a person by email + name + initial
   * activation. On a successful create, refresh the roster so the new row appears
   * (and so they're selectable on the Permissions screen for access assignment).
   */
  openAddUserDialog(): void {
    const ref = this.dialog.open(AddUserDialogComponent, { width: '480px' });
    ref.afterClosed().subscribe(created => {
      if (created) {
        this.usersService.refreshAllUsersRecords();
        this.snackBar.open('User added.', 'Dismiss', { duration: 3000 });
      }
    });
  }

  /**
   * Edit a user's first/last name. Reuses updateUserProfile (PUT /users/profile),
   * which the backend confirmed is admin-write-any + non-destructive (preserves
   * role/activation/deleted) + root-writing. Allowed on any row, including your own
   * (self-rename is permitted server-side). On save, refresh so the new name shows.
   */
  openEditNameDialog(user: ManagedUser): void {
    const ref = this.dialog.open(EditUserNameDialogComponent, {
      width: '440px',
      data: { email: user.email, firstName: user.firstName, lastName: user.lastName },
    });
    ref.afterClosed().subscribe(saved => {
      if (saved) {
        this.usersService.refreshAllUsersRecords();
        this.snackBar.open('Name updated.', 'Dismiss', { duration: 3000 });
      }
    });
  }

  /** True for the signed-in admin's own row — they can't delete themselves. */
  isOwnRow(email: string): boolean {
    return !!this.currentUserEmail &&
      normalizeEmail(email) === normalizeEmail(this.currentUserEmail);
  }

  isDeleting(email: string): boolean {
    return this.deletingEmails().has(email);
  }

  /**
   * Confirm + soft-delete a user. The backend marks the record `deleted` (retained
   * for audit) and filters it from reads, so on a successful delete we refresh the
   * roster and the row disappears. Self-delete is blocked (UI + this guard).
   */
  confirmDeleteUser(user: ManagedUser): void {
    if (this.isOwnRow(user.email) || this.isDeleting(user.email)) return;

    const ref = this.dialog.open(ConfirmDialogComponent, {
      width: '440px',
      data: {
        title: 'Remove user?',
        message: `Remove ${user.shortFriendlyName || user.email} (${user.email})? They will no longer appear in the user list or be able to access the app. This can be undone by an administrator.`,
        confirmLabel: 'Remove User',
        icon: 'person_remove',
        warn: true,
      },
    });

    ref.afterClosed().subscribe(async confirmed => {
      if (!confirmed) return;
      this.deletingEmails.update(s => new Set(s).add(user.email));
      try {
        await this.permissionsClient.deleteUser(user.email);
        this.usersService.refreshAllUsersRecords();
        this.snackBar.open(`${user.email} removed.`, 'Dismiss', { duration: 3000 });
      } catch (err) {
        this.snackBar.open(
          `Could not remove ${user.email}. ${this.errorMessage(err)}`,
          'Dismiss',
          { duration: 6000 });
      } finally {
        this.deletingEmails.update(s => {
          const next = new Set(s);
          next.delete(user.email);
          return next;
        });
      }
    });
  }

  onToggle(user: ManagedUser, newValue: boolean): Promise<void> {
    return this.saveRowChange(
      user,
      { activated: newValue },
      () => this.permissionsClient.setActivation(user.email, newValue),
      `${user.email} ${newValue ? 'activated' : 'deactivated'}.`);
  }

  isOptedOut(user: ManagedUser, category: EmailCategory): boolean {
    return this.liveRow(user).emailOptOuts.includes(category.key);
  }

  /** "All on" / "2 of 5 off" / "All off" — counts only categories we know about. */
  emailSummary(user: ManagedUser): string {
    const off = this.emailCategories.filter(c => this.isOptedOut(user, c)).length;
    if (off === 0) return 'All on';
    if (off === this.emailCategories.length) return 'All off';
    return `${off} of ${this.emailCategories.length} off`;
  }

  /**
   * Turn one category on or off. Keys we don't recognize (e.g. a category the
   * backend added before this list caught up) are kept, never dropped.
   */
  async onEmailCategoryToggle(
    row: ManagedUser, category: EmailCategory, receive: boolean, checkbox: MatCheckbox,
  ): Promise<void> {
    const user = this.liveRow(row);
    if (!receive && category.stopsActionEmails && !(await this.confirmActionEmailOptOut(user, category))) {
      checkbox.checked = true;  // Cancelled: put the box back.
      return;
    }
    const others = user.emailOptOuts.filter(k => k !== category.key);
    const next = receive ? others : [...others, category.key];
    return this.saveRowChange(
      user,
      { emailOptOuts: next },
      () => this.permissionsClient.setEmailOptOuts(user.email, next),
      `${category.label} emails ${receive ? 'on' : 'off'} for ${user.email}.`);
  }

  /**
   * Opting out of a category that carries "needs your action" emails means this
   * person stops being told when something is waiting on them. Allowed, but the
   * admin has to type "agree" first.
   */
  private async confirmActionEmailOptOut(user: ManagedUser, category: EmailCategory): Promise<boolean> {
    const name = user.shortFriendlyName || user.email;
    const ref = this.dialog.open(ConfirmDialogComponent, {
      width: '480px',
      data: {
        title: `Turn off all ${category.label} emails?`,
        message: `${name} will get no ${category.label.toLowerCase()} emails at all, including the ones saying something is waiting for their review or approval. Anything waiting on them can sit unnoticed.`,
        confirmLabel: 'Turn Off',
        icon: 'notifications_off',
        warn: true,
        typeToConfirm: 'agree',
      },
    });
    return (await firstValueFrom(ref.afterClosed())) === true;
  }

  /**
   * Shared save for the row controls: apply the change optimistically, disable
   * the row while the call is in flight, roll back if it fails.
   */
  private async saveRowChange(
    user: ManagedUser,
    patch: Partial<ManagedUser>,
    save: () => Promise<void>,
    successMessage: string,
  ): Promise<void> {
    const previous: Partial<ManagedUser> = {};
    for (const key of Object.keys(patch) as (keyof ManagedUser)[]) {
      (previous as any)[key] = user[key];
    }
    this.patchUser(user.email, patch);
    this.savingEmails.update(s => new Set(s).add(user.email));

    try {
      await save();
      this.snackBar.open(successMessage, 'Dismiss', { duration: 3000 });
    } catch (err) {
      this.patchUser(user.email, previous);
      this.snackBar.open(
        `Could not update ${user.email}. ${this.errorMessage(err)}`,
        'Dismiss',
        { duration: 6000 });
    } finally {
      this.savingEmails.update(s => {
        const next = new Set(s);
        next.delete(user.email);
        return next;
      });
    }
  }

  /**
   * The menu is handed the row as it was when it opened; saves replace row
   * objects, so read the current one by email.
   */
  private liveRow(user: ManagedUser): ManagedUser {
    return this.allUsers().find(u => u.email === user.email) ?? user;
  }

  private patchUser(email: string, patch: Partial<ManagedUser>): void {
    this.allUsers.update(users =>
      users.map(u => (u.email === email ? { ...u, ...patch } : u)));
  }

  private errorMessage(err: any): string {
    if (err?.status === 403) return 'You do not have permission to do this.';
    if (err?.status === 404) return 'This is not available yet for this org.';
    return 'Please try again.';
  }
}
